import React, { forwardRef, useEffect, useRef, useState } from "react";
import AlertCircleIcon from "@/shared/icons/AlertCircleIcon";
import GeneralInputWrapper from "../GeneralInputWrapper/GeneralInputWrapper";
import { UseFormRegisterReturn } from "react-hook-form";
import styles from "./FileUploadInput.module.scss";
import TrashIcon from "@/shared/icons/TrashIcon";
import ImportIcon from "@/shared/icons/ImportIcon";
import UploadSpinner from "./UploadSpinner";
import { uploadFileMutation } from "../../form-helpers";
import { FieldSettings, TextObject, WuiltContext } from "@/shared/types";

interface FileUploadProps {
  id?: string;
  isError?: boolean;
  registerProps?: UseFormRegisterReturn;
  fieldSettings: FieldSettings;
  formId: string;
  sectionId?: string;
  value?: string;
  sectionValueId?: string;
  formSubmitSucceeded?: boolean;
  onChange: (value: string | null | undefined) => void;
  uploadFileUrl: (formId?: string, sectionId?: string) => string;
  text: TextObject;
  elementId: string;
  wuiltContext: WuiltContext;
}

const FileUploadInput = forwardRef<any, FileUploadProps>((props, ref) => {
  const {
    id,
    isError,
    formId,
    fieldSettings,
    sectionId,
    formSubmitSucceeded,
    sectionValueId,
    onChange,
    uploadFileUrl,
    text,
    elementId,
    wuiltContext,
  } = props;
  const uploadInput = useRef<any>(null);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState<any>(null);
  const [error, setError] = useState(false);
  const acceptedFileTypes = fieldSettings.allowSpecificFileTypes
    ? `${fieldSettings.allowedExtensions}`
    : "";

  const allowedTypes = fieldSettings?.allowedExtensions;

  const onUploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(false);
    if (
      !uploadFileUrl ||
      !e.target.files?.[0] ||
      !id ||
      !formId ||
      !sectionId ||
      !sectionValueId
    ) {
      return;
    }
    const uploadedFile = e.target.files[0];
    const fileSize = uploadedFile.size / 1024 / 1024;
    const fileType = uploadedFile.name.split(".").pop();
    const siteData = wuiltContext?.props?.site;
    const planId = siteData?.plan?.id;
    const isBusiness = planId === 16 || planId === 19;
    const isUltimate = planId === 17 || planId === 20;
    const sizeLimit = isBusiness ? 10 : isUltimate ? 100 : 25;
    setUploading(true);
    setFile(null);
    if (
      fileSize < sizeLimit &&
      (!fieldSettings.allowSpecificFileTypes ||
        (fileType && allowedTypes?.includes(fileType)))
    ) {
      const fileUrl = await uploadFileMutation(
        uploadFileUrl,
        e.target.files?.[0],
        id,
        formId,
        sectionId,
        sectionValueId,
        elementId
      );
      setUploading(false);
      if (fileUrl?.uploaded_files) {
        setFile(uploadedFile);
        onChange(fileUrl?.uploaded_files[0]?.url);
      } else {
        setError(true);
        setUploading(false);
      }
    } else {
      setError(true);
      setUploading(false);
    }
  };

  const deleteFile = () => {
    setFile(null);
    onChange(undefined);
    uploadInput.current.value = "";
  };

  const openInputFilePopUp = () => {
    uploadInput.current.click();
  };

  useEffect(() => {
    if (formSubmitSucceeded) {
      setFile(null);
    }
  }, [formSubmitSucceeded]);

  return (
    <div ref={ref}>
      <div className={`${styles.fileUploadContainer}`}>
        <GeneralInputWrapper
          isError={error || isError}
          wuiltContext={wuiltContext}
        >
          <div
            aria-hidden="true"
            onClick={openInputFilePopUp}
            className={styles.inputContainer}
          >
            {uploading && <UploadSpinner />}

            {file ? (
              <span className={styles.fileName}>{file?.name}</span>
            ) : (
              <span className={styles.NoFileSelectedText}>
                {text[fieldSettings?.placeholder?.textId!]}
              </span>
            )}
          </div>
          {isError || error ? <AlertCircleIcon /> : null}
        </GeneralInputWrapper>
        {file ? (
          <div
            aria-hidden="true"
            onClick={deleteFile}
            className={styles.fileUploadButton}
          >
            <TrashIcon color="#D21C1C" />
          </div>
        ) : (
          <div
            className={styles.fileUploadButton}
            aria-hidden="true"
            onClick={openInputFilePopUp}
          >
            <ImportIcon color="#475467" />
          </div>
        )}
        <input
          id={id}
          accept={acceptedFileTypes}
          ref={uploadInput}
          type="file"
          className={styles.fileUploadInput}
          onChange={onUploadFile}
        />
      </div>
      <div className={styles.error}>
        {error && !isError && text[fieldSettings?.errorMessage?.textId!]}
      </div>
    </div>
  );
});

FileUploadInput.displayName = "FileUploadInput";
export default FileUploadInput;
